import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "./useCurrentUser";
import { supabase } from "@/integrations/supabase/client";

export interface UserPreferences {
  user_id: string;
  default_currency: string;
  default_language?: "cs" | "en" | "de" | string;
  theme: "light" | "dark" | "system";
  created_at: string;
  updated_at: string;
}

export interface UserPreferencesUpdate {
  default_currency?: string;
  default_language?: "cs" | "en" | "de" | string;
  theme?: "light" | "dark" | "system";
}

/**
 * Fetch user preferences from profiles table
 */
export function useUserPreferences() {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: ["user_preferences", user?.id],
    queryFn: async () => {
      console.log("[useUserPreferences] Fetching preferences...");
      if (!user) return null;

      try {
        // Query by user_id (not id) - profiles table uses user_id to match auth.uid()
        const { data, error } = await supabase
          .from("profiles")
          .select("user_id, default_currency, default_language, theme, created_at, updated_at")
          .eq("user_id", user.id)
          .single();

        console.log("[useUserPreferences] Result:", {
          hasData: !!data,
          hasError: !!error,
          errorCode: error?.code,
        });

        if (error) {
          // If profile doesn't exist, return defaults
          if (error.code === "PGRST116") {
            console.log("[useUserPreferences] Profile not found, returning defaults");
            return {
              user_id: user.id,
              default_currency: "CZK",
              default_language: "en",
              theme: "dark" as const,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            } as UserPreferences;
          }
          console.error("[useUserPreferences] Error:", error);
          // Return defaults on error instead of throwing
          return {
            user_id: user.id,
            default_currency: "CHF",
            default_language: "en",
            theme: "dark" as const,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as UserPreferences;
        }

        return {
          user_id: data.user_id || user.id,
          default_currency: data.default_currency || "CZK",
          default_language: data.default_language || "en",
          theme: (data.theme as "light" | "dark" | "system") || "dark",
          created_at: data.created_at || new Date().toISOString(),
          updated_at: data.updated_at || new Date().toISOString(),
        } as UserPreferences;
      } catch (err: any) {
        console.error("[useUserPreferences] Exception:", err);
        // Return defaults on error
        return {
          user_id: user.id,
          default_currency: "CHF",
          default_language: "en",
          theme: "dark" as const,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as UserPreferences;
      }
    },
    enabled: !!user,
  });
}

/**
 * Update user preferences in profiles table
 */
export function useUpdateUserPreferences() {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation({
    mutationFn: async (updates: UserPreferencesUpdate) => {
      console.log("[useUpdateUserPreferences] Updating preferences:", updates);
      if (!user) throw new Error("User not authenticated");

      // Prepare update data - remove undefined values
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      // Only include defined values
      if (updates.default_currency !== undefined) {
        updateData.default_currency = updates.default_currency;
      }
      if (updates.default_language !== undefined) {
        updateData.default_language = updates.default_language;
      }
      if (updates.theme !== undefined) {
        updateData.theme = updates.theme;
      }

      // Update existing profile (profile should always exist due to trigger, but handle errors gracefully)
      const { data, error } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("user_id", user.id)
        .select()
        .single();

      console.log("[useUpdateUserPreferences] Result:", {
        hasData: !!data,
        hasError: !!error,
        errorCode: error?.code,
      });

      if (error) {
        console.error("[useUpdateUserPreferences] Error:", error);
        throw error;
      }

      return {
        user_id: data.user_id || user.id,
        default_currency: data.default_currency || "CHF",
        default_language: data.default_language || "en",
        theme: (data.theme as "light" | "dark" | "system") || "dark",
        created_at: data.created_at || new Date().toISOString(),
        updated_at: data.updated_at || new Date().toISOString(),
      } as UserPreferences;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_preferences"] });
    },
  });
}
